import React, { createContext, useContext, useState, useCallback } from 'react';

export interface PieDataItem {
  label: string;
  value: number;
  color: string;
}

export interface Dataset {
  label: string;
  data: number[];
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
}

export interface GeoPoint {
  geohash: string;
  value: number;
}

export interface MapAIItem {
  id: string;
  title?: string;
  description?: string;
  type: 'heat_map' | 'marker_map' | 'cluster_map';
  points: GeoPoint[];
}

export interface ChartAIItem {
  type: 'bar_chart' | 'line_chart' | 'pie_chart' | string;
  title?: string;
  description?: string;
  data: {
    labels?: string[];
    datasets?: Dataset[];
    pieData?: PieDataItem[];
  };
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  suggestions?: string[];
  chartAI?: ChartAIItem[];
  mapAI?: MapAIItem[];
}

interface ChatStore {
  messages: Record<string, ChatMessage[]>;
  addMessage: (sessionId: string, message: ChatMessage) => void;
  getMessages: (sessionId: string) => ChatMessage[];
  clearMessages: (sessionId: string) => void;
}

const ChatStoreContext = createContext<ChatStore | null>(null);

export const ChatStoreProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [messages, setMessages] = useState<Record<string, ChatMessage[]>>({});

  const addMessage = useCallback((sessionId: string, message: ChatMessage) => {
    setMessages(prev => ({
      ...prev,
      [sessionId]: [...(prev[sessionId] || []), message],
    }));
  }, []);

  const getMessages = useCallback(
    (sessionId: string) => {
      return messages[sessionId] || [];
    },
    [messages],
  );

  const clearMessages = useCallback((sessionId: string) => {
    setMessages(prev => {
      const newMessages = { ...prev };
      delete newMessages[sessionId];
      return newMessages;
    });
  }, []);

  return (
    <ChatStoreContext.Provider
      value={{
        messages,
        addMessage,
        getMessages,
        clearMessages,
      }}
    >
      {children}
    </ChatStoreContext.Provider>
  );
};

export const useChatStore = () => {
  const context = useContext(ChatStoreContext);
  if (!context) {
    throw new Error('useChatStore must be used within a ChatStoreProvider');
  }
  return context;
};
